import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { withStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';

const styles = (theme) => ({
  fullWidth: {
    width: '100%',
  },
  infoTip: {
    fontSize: '14px',
    padding: '16px 16px 16px 16px',
    background: theme.palette.text.primary,
    color: theme.palette.background.paper,
    fontWeight: 'normal',
    fontFamily: 'Rubik',
    borderRadius: 12,
    borderColor: theme.palette.border.main,
  },
  infoTipInverted: {
    fontSize: '14px',
    padding: '16px 16px 16px 16px',
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontWeight: 'normal',
    fontFamily: 'Rubik',
    borderRadius: 12,
    borderColor: theme.palette.border.main,
  },
  infoArrow: {
    color: theme.palette.text.primary,
  },
  infoArrowInverted: {
    color: theme.palette.background.paper,
  },
});

class QTip extends React.Component {
  constructor(props) {
    super(props);
    this.wrapRef = null;
    this.title = undefined;
    this.state = {
      overflow: false,
    };
    this.defaultId = uuidv4();
  }

  componentDidMount() {
    if (this.props.wrapOnly) {
      this.checkOverflow();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.wrapRef = null;
    if (nextProps.wrapOnly && nextProps.title !== this.props.title) {
      this.setState({ overflow: false });
      this.checkOverflow();
    }
  }

  isElementOverflow = (element) =>
    element && (element.clientWidth < element.scrollWidth || element.clientHeight < element.scrollHeight);

  checkOverflow = () => {
    const element = this.wrapRef || document.getElementById(`QtipId:${this.props.wrapId || this.defaultId}`);
    this.wrapRef = element;

    const findTextElement = (currentElement) => {
      if (currentElement) {
        for (const childElement of currentElement.children) {
          // eslint-disable-line no-restricted-syntax
          const textElement = findTextElement(childElement);
          if (textElement) {
            return textElement;
          }
        }
        if (currentElement.innerText || currentElement.value) {
          return currentElement;
        }
      }
      return undefined;
    };
    const textElement = findTextElement(element);
    const overflow = this.isElementOverflow(textElement);
    this.title = textElement?.innerText || String(textElement?.value);
    this.setState({ overflow });
  };

  render() {
    const {
      children,
      title = this.title,
      html,
      classes,
      className,
      popperProps,
      arrowClass,
      tooltipClass,
      noTippyTip, // TODO: keep it to reduce patch size risk, but prop does nothing - refactor/remove
      enableOnMobile,
      fullWidth,
      placement,
      solidTheme,
      wrapOnly,
      wrapId,
      variant,
      ...other
    } = this.props;
    const id = wrapId || this.defaultId;

    if (
      (!isMobile || enableOnMobile) &&
      (html || (typeof title === 'string' && title.trim().length > 0)) &&
      (!wrapOnly || this.state.overflow)
    ) {
      return (
        <Tooltip
          enterDelay={500}
          id={`tooltip:${id}`}
          leaveDelay={200}
          placement={placement || 'top'}
          title={html || title || ''}
          classes={{
            tooltip: solidTheme
              ? classNames(variant === 'inverted' ? classes.infoTipInverted : classes.infoTip, tooltipClass)
              : tooltipClass,
            arrow: solidTheme
              ? classNames(variant === 'inverted' ? classes.infoArrowInverted : classes.infoArrow, arrowClass)
              : arrowClass,
          }}
          PopperProps={popperProps}
          {...(variant === 'inverted' && { slotProps: { tooltip: { sx: { boxShadow: 3 } } } })}
          {...other}
        >
          {children}
        </Tooltip>
      );
    }
    return (
      <span id={`QtipId:${id}`} className={fullWidth ? classNames(classes.fullWidth, className) : className}>
        {children}
      </span>
    );
  }
}

QTip.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  html: PropTypes.object,
  wrapOnly: PropTypes.bool,
  wrapId: PropTypes.string,
  classes: PropTypes.object,
  arrowClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  enterDelay: PropTypes.number,
  placement: PropTypes.string,
  position: PropTypes.string, // accepts either one of these as an override
  popperProps: PropTypes.object,
  noTippyTip: PropTypes.bool,
  enableOnMobile: PropTypes.bool,
  fullWidth: PropTypes.bool,
  solidTheme: PropTypes.bool,
  tooltipClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  variant: PropTypes.string, // 'standard' 'inverted'
};

export default withStyles(QTip, styles);
